import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { keys, getLocaleDirection } from "@/lib/i18n/portal";
import { Surface } from "@/shared/layout/Surface";
import { AppShell } from "@/shared/layout/AppShell";
import { RouteErrorBoundary } from "@/shared/error/RouteErrorBoundary";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { NotificationsProvider } from "@/modules/notifications/NotificationsProvider";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "./locale";

export function ProtectedLayout() {
  const locale = useLocale();
  const { storeSlug = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useTranslations();
  const { data, isLoading, error } = useSessionQuery({ storeSlug });

  useEffect(() => {
    if (
      error instanceof ApiJsonClientError &&
      (error.status === 401 || error.status === 403)
    ) {
      navigate(`/${locale}/login`, { replace: true });
      return;
    }

    const authenticatedStoreSlug = data?.seller?.storeSlug?.trim();
    if (authenticatedStoreSlug && authenticatedStoreSlug !== storeSlug) {
      navigate(`/${locale}/${authenticatedStoreSlug}`, { replace: true });
    }
  }, [data?.seller?.storeSlug, error, locale, navigate, storeSlug]);

  if (
    error instanceof ApiJsonClientError &&
    (error.status === 401 || error.status === 403)
  ) {
    return <Navigate to={`/${locale}/login`} replace />;
  }

  const authenticatedStoreSlug = data?.seller?.storeSlug?.trim();
  if (authenticatedStoreSlug && authenticatedStoreSlug !== storeSlug) {
    return <Navigate to={`/${locale}/${authenticatedStoreSlug}`} replace />;
  }

  if (isLoading) {
    return (
      <Surface
        locale={locale}
        direction={getLocaleDirection(locale)}
        title={t(keys.common.loading)}
      >
        <div className="text-sm text-[color:var(--portal-muted)]">
          {t(keys.common.loading)}
        </div>
      </Surface>
    );
  }

  // Auto-redirect to quick setup on first login when store has no config
  const overview = data?.overview;
  const needsOnboarding =
    overview &&
    !overview.hasCategories &&
    !overview.hasActivePaymentOptions &&
    !overview.hasProducts;
  const isOnQuickSetup = location.pathname.endsWith("/quick-setup");

  if (needsOnboarding && !isOnQuickSetup) {
    return <Navigate to={`/${locale}/${storeSlug}/quick-setup`} replace />;
  }

  return (
    <NotificationsProvider
      sellerId={data?.seller?.id}
      storeId={data?.seller?.storeId}
    >
      <AppShell locale={locale} storeSlug={storeSlug} loginUrl={`/${locale}/login`}>
        <RouteErrorBoundary>
          <div key={location.pathname}>
            <Outlet />
          </div>
        </RouteErrorBoundary>
      </AppShell>
    </NotificationsProvider>
  );
}
